import { displayChord } from '../chords/chord'
import type { Notation } from '../chords/notes'
import type { Line, RevisionContent, Section, SectionKind } from '../schema/v1'

/**
 * Exporta a texto con los acordes encima de la letra, para imprimir o pegar en otro sitio. Es
 * el mismo formato que lee `parseChordsOverLyrics`: cada sección se escribe una vez con su
 * etiqueta y las repeticiones quedan como una etiqueta suelta («Estribillo»).
 */
export interface ChordSheetOptions {
  /** Notación con la que se escriben los acordes. Por defecto, la de la ficha. */
  notation?: Notation
  /** Cabecera con tono, cejilla, tempo y compás. Por defecto, sí. */
  header?: boolean
}

export interface ChordSheetResult {
  text: string
  /** Lo que el texto plano no puede guardar, explicado para la persona. */
  losses: string[]
}

const DEFAULT_LABELS: Record<SectionKind, string | null> = {
  intro: 'Intro',
  verse: 'Estrofa',
  prechorus: 'Preestribillo',
  chorus: 'Estribillo',
  bridge: 'Puente',
  instrumental: 'Instrumental',
  solo: 'Solo',
  interlude: 'Interludio',
  outro: 'Final',
  tab: 'Tablatura',
  other: null,
}

/** La etiqueta con la que se escribe una sección, o null si no tiene ninguna. */
function labelOf(section: Section): string | null {
  return section.label ?? DEFAULT_LABELS[section.kind]
}

/** Línea de acordes alineada con la letra. Devuelve cuántos tuvo que correr para no pisarse. */
function alignChords(
  line: Pick<Line, 'chords'>,
  show: (symbol: string) => string,
): { text: string; shifted: number } {
  let text = ''
  let shifted = 0
  for (const anchor of line.chords) {
    const symbol = show(anchor.chord)
    const earliest = text === '' ? 0 : text.length + 1
    const column = Math.max(anchor.at, earliest)
    if (column !== anchor.at) shifted += 1
    text += ' '.repeat(column - text.length) + symbol
  }
  return { text, shifted }
}

export function serializeChordSheet(
  content: RevisionContent,
  options: ChordSheetOptions = {},
): ChordSheetResult {
  const notation = options.notation ?? content.notation
  const show = (symbol: string) => displayChord(symbol, notation)
  const losses: string[] = []
  let shifted = 0

  const blocks: string[][] = []

  if (options.header !== false) {
    const header: string[] = []
    if (content.shapeKey) header.push(`Tono: ${show(content.shapeKey)}`)
    if (content.capo > 0) header.push(`Cejilla en ${content.capo}`)
    if (content.tempo.reference) header.push(`Tempo: ${content.tempo.reference.bpm} BPM`)
    if (content.timeSignature) header.push(`Compás: ${content.timeSignature}`)
    if (content.tuning) header.push(`Afinación: ${content.tuning}`)
    if (header.length > 0) blocks.push(header)
  }

  const body = (section: Section): string[] => {
    const output: string[] = []
    const label = labelOf(section)
    if (label) output.push(`${label}:`)
    for (const line of section.lines) {
      if (line.kind === 'comment') {
        output.push(`(${line.text})`)
      } else if (section.kind === 'tab' || line.chords.length === 0) {
        output.push(line.text)
      } else if (line.text.trim() === '') {
        output.push(line.chords.map((anchor) => show(anchor.chord)).join('  '))
      } else {
        const aligned = alignChords(line, show)
        shifted += aligned.shifted
        output.push(aligned.text, line.text)
      }
    }
    return output
  }

  const byId = new Map(content.sections.map((section) => [section.id, section]))
  const emitted = new Set<string>()
  let repeatsWithoutLabel = 0
  for (const occurrence of content.sequence) {
    const section = byId.get(occurrence.section)
    if (!section) continue
    if (!emitted.has(section.id)) {
      emitted.add(section.id)
      blocks.push(body(section))
    } else if (labelOf(section)) {
      blocks.push([`${labelOf(section)}:`])
    } else {
      repeatsWithoutLabel += 1
      blocks.push(body(section))
    }
  }
  for (const section of content.sections) {
    if (!emitted.has(section.id)) blocks.push(body(section))
  }

  if (shifted > 0) losses.push(`${shifted} acordes que se pisaban, separados un espacio`)
  if (repeatsWithoutLabel > 0)
    losses.push('Las repeticiones de partes sin etiqueta se copian enteras')
  if (content.beatMap.status !== 'absent')
    losses.push('El mapa de tiempos (duración de cada acorde)')
  if (content.soundingKey) losses.push('La tonalidad que suena')
  if (content.beatUnit) losses.push('La unidad de pulso')
  if (content.notes) losses.push('Las notas')
  if (Object.values(content.provenance).some((entry) => entry.kind !== 'import')) {
    losses.push('La procedencia de cada dato')
  }

  return { text: blocks.map((block) => block.join('\n')).join('\n\n') + '\n', losses }
}
